'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { Col } from '@/components/ui/col'
import { Container } from '@/components/ui/container'
import { TypographyH2, TypographyH3, TypographySpan } from '@/components/ui/typography'
import { Section } from '@/components/ui/section'
import { cn } from '@/lib/utils'
import {
  getRoleStatusKey,
  ROLE_STATUS_TEXT_TONE,
  type CareerApiItem,
  type CareersApiResponse,
} from '@/lib/api/careers'
import type { DepartmentApiItem } from '@/lib/api/departments'

import { careersListQuery } from '../api/careers'

const ALL_TAB_KEY = 'all'

function getRoleBadge(role: CareerApiItem): string {
  return role.employmentType ?? ''
}

type Props = {
  initialAllRoles: CareersApiResponse
  departments: DepartmentApiItem[]
}

export function CompanyCareersOpenRolesSection({ initialAllRoles, departments }: Props) {
  const t = useTranslations('companyCareersPage')
  const locale = useLocale()
  const [activeSlug, setActiveSlug] = useState<string>(ALL_TAB_KEY)

  const departmentSlug = activeSlug === ALL_TAB_KEY ? undefined : activeSlug
  const { data, isError, isLoading } = useQuery({
    ...careersListQuery(departmentSlug, locale),
    initialData: departmentSlug === undefined ? initialAllRoles : undefined,
    placeholderData: keepPreviousData,
  })
  const roles = isError ? [] : (data?.data ?? [])
  const showSkeleton = isLoading && roles.length === 0

  return (
    <Section id="open-roles" className="py-16 md:py-24 lg:py-32 bg-white border-b border-gray-200">
      <Container size="site" className="max-w-[1000px]">
        <Col className="mb-12 md:flex-row md:items-end justify-between gap-6 border-b border-gray-200 pb-6">
          <div>
            <TypographySpan className="font-mono text-fs-md font-bold tracking-widest text-brand-red uppercase block mb-2" tone="brand" variant="inherit">
              {t('roles.pre')}
            </TypographySpan>
            <TypographyH2 className="text-3xl font-bold text-charcoal-900 tracking-tight" scale="inherit">{t('roles.title')}</TypographyH2>
          </div>

          <div className="flex flex-wrap gap-4 font-mono text-xs text-charcoal-500 uppercase tracking-widest">
            <button
              key={ALL_TAB_KEY}
              type="button"
              onClick={() => setActiveSlug(ALL_TAB_KEY)}
              className={cn(
                'pb-1 transition-colors',
                activeSlug === ALL_TAB_KEY
                  ? 'text-charcoal-900 font-bold border-b-2 border-charcoal-900'
                  : 'hover:text-charcoal-900 cursor-pointer',
              )}
            >
              {t('roles.tab1')}
            </button>
            {departments.map((dept) => {
              const isActive = dept.slug === activeSlug
              return (
                <button
                  key={dept.slug}
                  type="button"
                  onClick={() => setActiveSlug(dept.slug)}
                  className={cn(
                    'pb-1 transition-colors',
                    isActive
                      ? 'text-charcoal-900 font-bold border-b-2 border-charcoal-900'
                      : 'hover:text-charcoal-900 cursor-pointer',
                  )}
                >
                  {dept.name}
                </button>
              )
            })}
          </div>
        </Col>

        <Col className="gap-4">
          {showSkeleton ? (
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="job-card border border-gray-200 p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-lg animate-pulse"
                >
                  <div className="flex-1 space-y-3">
                    <div className="h-5 w-2/3 rounded bg-charcoal-100" />
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-20 rounded bg-charcoal-100" />
                      <div className="h-3 w-40 rounded bg-charcoal-100" />
                    </div>
                  </div>
                  <div className="h-4 w-24 rounded bg-charcoal-100" />
                </div>
              ))}
            </>
          ) : roles.length === 0 ? (
            <p className="text-sm text-charcoal-500 font-mono">{t('roles.empty')}</p>
          ) : null}
          {!showSkeleton && roles.map((role) => {
            const badge = getRoleBadge(role)
            const statusKey = getRoleStatusKey(role)
            return (
              <Link
                key={role.slug}
                href={`/${locale}/company/careers/${role.slug}`}
                className="job-card group relative border border-gray-200 p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer rounded-lg hover:shadow-md"
              >
                {statusKey ? (
                  <TypographySpan
                    className={cn(
                      'absolute right-4 top-4 font-mono text-xs font-bold uppercase tracking-widest',
                      ROLE_STATUS_TEXT_TONE[statusKey],
                    )}
                    variant="inherit"
                  >
                    {t(`status.${statusKey}`)}
                  </TypographySpan>
                ) : null}
                <div className="flex-1">
                  <TypographyH3 className="text-lg lg:text-xl font-bold text-charcoal-900 group-hover:text-brand-red transition-colors mb-2" scale="inherit">
                    {role.title}
                  </TypographyH3>
                  <div className="flex items-center gap-3 text-xs font-mono text-charcoal-500">
                    {badge ? (
                      <>
                        <TypographySpan className="bg-gray-100 border border-gray-200 px-2 py-1 rounded-sm text-charcoal-900 font-bold uppercase" variant="inherit">
                          {badge}
                        </TypographySpan>
                        <TypographySpan variant="inherit">•</TypographySpan>
                      </>
                    ) : null}
                    <TypographySpan variant="inherit">{role.location ?? ''}{role.workMode ? ` / ${role.workMode}` : ''}</TypographySpan>
                  </div>
                </div>
                <div className="flex items-center text-fs-sm font-bold text-charcoal-900 uppercase tracking-widest group-hover:text-brand-red transition-colors">
                  <TypographySpan variant="inherit">{t('roles.view')}</TypographySpan>
                  <TypographySpan className="ml-2 transform group-hover:translate-x-1 transition-transform" variant="inherit">&rarr;</TypographySpan>
                </div>
              </Link>
            )
          })}
        </Col>
      </Container>
    </Section>
  )
}
